import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { improveApi } from '../../api/improve'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate, formatRelative } from '../../lib/format'
import { ruleSentence } from '../../lib/improve'
import type { ImprovementRun, Lesson } from '../../types/improve'

// Every night the loop has run, newest first: what it read, what it applied, what it handed back, and the
// lessons behind each. A negative lesson is shown as prominently as a positive one — a loop that only shows
// its wins is a loop nobody can audit (rule 4: negative results are kept).

export function RunsPanel({ onOpenCorrection }: { onOpenCorrection: (correctionId: string) => void }) {
  const spoke = useSpoke()
  const q = useQuery({ queryKey: ['improve-runs', spoke], queryFn: () => improveApi.runs(spoke, 30), staleTime: 30_000 })
  const runs = q.data ?? []

  return (
    <div className="card runs-card">
      <div className="card-head">
        <div>
          <h2>Nightly runs <span className="count">{runs.length}</span></h2>
          <p className="muted">
            The self-improve job runs first each night, before the mail is re-read, so everything after it runs under the
            rules it just proved. One applied change per correction; every candidate that failed is kept with its reason.
          </p>
        </div>
      </div>

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && <div className="refusal"><strong>Unavailable</strong><p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error.message}</p></div>}
      {q.isSuccess && runs.length === 0 && (
        <div className="empty-note">
          <strong>No run yet.</strong>
          <p className="muted">The first run happens on the next scheduled pass with at least one open correction on the ledger.</p>
        </div>
      )}

      <ul className="run-list">
        {runs.map((r) => <RunCard key={r.runId} run={r} onOpenCorrection={onOpenCorrection} />)}
      </ul>
    </div>
  )
}

function RunCard({ run, onOpenCorrection }: { run: ImprovementRun; onOpenCorrection: (correctionId: string) => void }) {
  const negative = run.lessons.filter((l) => l.wasNegative).length
  return (
    <li className={`run-card${run.applied > 0 ? ' productive' : ''}`}>
      <div className="run-head">
        <div>
          <p className="kicker">{formatDate(run.ranAt)} · {formatRelative(run.ranAt)}{run.trigger === 'manual' ? ' · started by hand' : ''}</p>
          <p className="run-outcome">{run.outcome}</p>
        </div>
        {run.jobId && <Link className="button small" to={`/jobs/${encodeURIComponent(run.jobId)}`}>Console</Link>}
      </div>

      <div className="run-tallies">
        <span className="run-tally"><strong>{run.gathered}</strong><span>read</span></span>
        <span className={`run-tally ${run.applied > 0 ? 'ok' : ''}`}><strong>{run.applied}</strong><span>applied</span></span>
        <span className={`run-tally ${run.needsHuman > 0 ? 'warn' : ''}`}><strong>{run.needsHuman}</strong><span>handed back</span></span>
        <span className="run-tally"><strong>{run.declined}</strong><span>declined</span></span>
        <span className={`run-tally ${negative > 0 ? 'negative' : ''}`}><strong>{negative}</strong><span>dead ends kept</span></span>
      </div>

      {run.lessons.length === 0 ? (
        <p className="muted small">Nothing was learned on this pass.</p>
      ) : (
        <ul className="lesson-list">
          {run.lessons.map((l) => <LessonRow key={l.lessonId} lesson={l} onOpenCorrection={onOpenCorrection} />)}
        </ul>
      )}
    </li>
  )
}

function LessonRow({ lesson, onOpenCorrection }: { lesson: Lesson; onOpenCorrection: (correctionId: string) => void }) {
  const applied = lesson.proposals.find((p) => p.applied) ?? null
  return (
    <li className={lesson.wasNegative ? 'negative' : 'positive'}>
      <span className={`tag sig ${lesson.wasNegative ? 'high' : 'ok'}`}>{lesson.wasNegative ? 'dead end' : 'learned'}</span>
      <strong>{lesson.summary}</strong>
      <p className="muted small">{lesson.detail}</p>
      {applied && <p className="lesson-rule">{ruleSentence(applied.kind, applied.definition) ?? `A ${applied.kind} rule is now live.`}</p>}
      {lesson.correctionId && (
        <button className="link-button small" onClick={() => onOpenCorrection(lesson.correctionId!)}>Open the correction behind it</button>
      )}
    </li>
  )
}
